import React, {useState, useEffect, ChangeEvent, useCallback, ChangeEventHandler, Ref, useRef} from 'react';
import {useFormContext} from "react-hook-form";
import dynamic from "next/dynamic";
import {InputMaskProps, format} from "@react-input/mask";
import {IGenericInputProps} from "@/@types/GenericInputProps";

interface TextInputProps extends IGenericInputProps {
    id?: string;
    required?: boolean;
    debounce?: number | null;
    placeholder?: string;
    validation?: boolean;
    className?: string;
    mask?: InputMaskProps["mask"];
    replacement?: InputMaskProps["replacement"];
    track?: InputMaskProps["track"];
    disabled?: boolean;
    autoComplete?: string;
    ref?: Ref<any>;
}

export default function TextInput({
    id,
    value,
    onChange,
    fieldName,
    required,
    debounce,
    placeholder,
    validation = undefined,
    className = '',
    mask,
    replacement,
    track,
    disabled = false,
    autoComplete,
    inputProps = {},
    ref = null,
}: TextInputProps) {
    const [internalValue, setInternalValue] = useState<string>(String(value));

    useEffect(() => {
        setInternalValue(
          mask && replacement ? format(String(value), {mask, replacement}) : String(value)
        );
    }, [value]);

    const debouncedOnChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const rawValue = e.target.value;
            setInternalValue(rawValue);

            const handler = setTimeout(() => {
                onChange({
                    value: e.target.value,
                    fieldName,
                });
            }, debounce || 0);

            return () => {
                clearTimeout(handler);
            };
        },
        [onChange, debounce, value]
    );

    const inputClassName = `flex-grow h-full px-4 py-2 outline-none rounded-md ${className}`
    return (
        <>
            {validation
                ? <TextInputWithValidation
                    id={id}
                    ref={ref}
                    value={internalValue}
                    onChange={onChange}
                    internalValue={internalValue}
                    debouncedOnChange={debouncedOnChange}
                    fieldName={fieldName}
                    validation={validation}
                    required={required}
                    placeholder={placeholder}
                    mask={mask}
                    replacement={replacement}
                    track={track}
                    className={inputClassName}
                    disabled={disabled}
                    autoComplete={autoComplete}
                    inputProps={inputProps}
                />
                :
                <input
                    ref={ref}
                    id={id}
                    type="text"
                    value={internalValue}
                    onChange={debouncedOnChange}
                    name={fieldName}
                    required={required}
                    autoComplete={autoComplete}
                    className={inputClassName}
                    placeholder={placeholder}
                    disabled={disabled}
                    {...inputProps}
                />}
        </>
    );
}

const InputMask = dynamic(() => import('@react-input/mask').then(mod => mod.InputMask));

function TextInputWithValidation(props: TextInputProps & { internalValue: string, debouncedOnChange: ChangeEventHandler }) {
    const { register, formState: { errors }} = useFormContext()
    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
        props.debouncedOnChange(e)
    }

    if (props.mask) {
        return <InputMask
            id={props.id}
            mask={props.mask}
            replacement={props.replacement}
            track={props.track}
            value={props.internalValue}
            required={props.required}
            autoComplete={props.autoComplete}
            className={props.className}
            disabled={props.disabled}
            placeholder={props.placeholder}
            {...props.inputProps}
            {...register(props.fieldName as string)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
        />
    }


    return <input
            id={props.id}
            type="text"
            value={props.internalValue}
            required={props.required}
            autoComplete={props.autoComplete}
            className={props.className}
            placeholder={props.placeholder}
            disabled={props.disabled}
            {...props.inputProps}
            {...register(props.fieldName as string)}
            onChange={(e) => handleOnChange(e)}
        />
}
